import React, { Suspense, useEffect, useState } from 'react';
import { apiFetch } from '../api';

const AIRecommendationsBlock = React.lazy(() => import('../components/AIRecommendationsBlock'));
const AIMLDemandSummaryBlock = React.lazy(() => import('../components/AIMLDemandSummaryBlock'));
const AIContentMLSummaryBlock = React.lazy(() => import('../components/AIContentMLSummaryBlock'));
const AIClusterSupplyBlock = React.lazy(() => import('../components/AIClusterSupplyBlock'));
const AIPromoMonitorBlock = React.lazy(() => import('../components/AIPromoMonitorBlock'));
const AIRetrospectiveBlock = React.lazy(() => import('../components/AIRetrospectiveBlock'));
const AIHypothesesMemoryBlock = React.lazy(() => import('../components/AIHypothesesMemoryBlock'));
const AIProductsMonitorBlock = React.lazy(() => import('../components/AIProductsMonitorBlock'));
const AIProductCardHistoryBlock = React.lazy(() => import('../components/AIProductCardHistoryBlock'));
const AIReturnsAnalyticsBlock = React.lazy(() => import('../components/AIReturnsAnalyticsBlock'));
const AIExclusionsBlock = React.lazy(() => import('../components/AIExclusionsBlock'));
const AIAdminSettingsBlock = React.lazy(() => import('../components/AIAdminSettingsBlock'));
const AICompetitorCandidatesBlock = React.lazy(
  () => import('../components/AICompetitorCandidatesBlock')
);
const PriceCalculatorModal = React.lazy(() => import('../components/PriceCalculatorModal'));

type AIAgentsTab =
  | 'recommendations'
  | 'products'
  | 'competitors'
  | 'ads'
  | 'retro'
  | 'returns'
  | 'stocks'
  | 'models'
  | 'settings';

export interface AIRuntimeReadiness {
  ready: boolean;
  backend?: {
    ready?: boolean;
    uptime_seconds?: number;
    min_uptime_seconds?: number;
  } | null;
  tenant_settings?: {
    ready?: boolean;
    missing?: string[];
  } | null;
  ai_agent?: {
    reachable?: boolean;
    warmed_up?: boolean;
    uptime_seconds?: number;
    min_uptime_seconds?: number;
  } | null;
  blocking_reasons?: string[];
  warning_reasons?: string[];
}

const AIAgentsPage: React.FC = () => {
  const [tab, setTab] = useState<AIAgentsTab>('recommendations');
  const [runtimeReadiness, setRuntimeReadiness] = useState<AIRuntimeReadiness | null>(null);
  const [runtimeReadinessLoading, setRuntimeReadinessLoading] = useState(true);
  const [isPriceCalcOpen, setPriceCalcOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        if (!cancelled) {
          setRuntimeReadinessLoading(true);
        }
        const res = await apiFetch('/api/ai/runtime-readiness');
        if (!res.ok) {
          if (!cancelled) {
            setRuntimeReadiness(null);
          }
          return;
        }
        const data = (await res.json()) as AIRuntimeReadiness;
        if (!cancelled) {
          setRuntimeReadiness(data && typeof data === 'object' ? data : null);
        }
      } catch {
        if (!cancelled) {
          setRuntimeReadiness(null);
        }
      } finally {
        if (!cancelled) {
          setRuntimeReadinessLoading(false);
        }
      }
    };

    load().catch(console.error);
    const intervalId = window.setInterval(() => {
      load().catch(console.error);
    }, 15000);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, []);

  const isReady = Boolean(runtimeReadiness?.ready);
  const hasReadinessData = Boolean(runtimeReadiness);
  const indicatorClass = runtimeReadinessLoading
    ? 'bg-slate-100 text-slate-600 border-slate-200'
    : !hasReadinessData
      ? 'bg-rose-50 text-rose-700 border-rose-200'
      : isReady
      ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
      : 'bg-amber-50 text-amber-700 border-amber-200';
  const blockingReasons = Array.isArray(runtimeReadiness?.blocking_reasons)
    ? runtimeReadiness?.blocking_reasons || []
    : [];
  const warningReasons = Array.isArray(runtimeReadiness?.warning_reasons)
    ? runtimeReadiness?.warning_reasons || []
    : [];
  const readinessMessage = runtimeReadinessLoading
    ? 'Проверка состояния AI-контура'
    : !hasReadinessData
      ? 'Не удалось получить состояние AI-контура'
      : isReady
      ? (warningReasons.length > 0
          ? 'AI-контур готов к расчетам, часть дополнительных сервисов недоступна'
          : 'AI-контур готов к расчетам')
      : 'AI-контур прогревается, запуск расчетов временно заблокирован';
  const formatSecondsRemaining = (value: number | null | undefined): string => {
    const normalized = Math.max(0, Math.round(Number(value || 0)));
    if (normalized <= 0) return '0 сек';
    if (normalized >= 3600) {
      return `${Math.ceil(normalized / 3600)} ч`;
    }
    if (normalized >= 60) {
      return `${Math.ceil(normalized / 60)} мин`;
    }
    return `${normalized} сек`;
  };
  const backendRemainingSeconds = runtimeReadiness?.backend
    ? Math.max(
        0,
        Number(runtimeReadiness.backend.min_uptime_seconds || 0) - Number(runtimeReadiness.backend.uptime_seconds || 0),
      )
    : 0;
  const aiAgentRemainingSeconds = runtimeReadiness?.ai_agent
    ? Math.max(
        0,
        Number(runtimeReadiness.ai_agent.min_uptime_seconds || 0) - Number(runtimeReadiness.ai_agent.uptime_seconds || 0),
      )
    : 0;
  const tabContentFallback = (
    <div className="max-w-7xl mx-auto rounded-2xl border border-slate-200 bg-white px-4 py-8 text-sm text-slate-500 shadow">
      Загрузка AI модуля...
    </div>
  );

  const runtimeReadinessBlock = (
    <div className={`mb-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl border px-4 py-3 text-sm ${indicatorClass}`}>
      <div className="font-medium">{readinessMessage}</div>
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className="rounded-full bg-white/70 px-2 py-1">
          Backend: {!hasReadinessData ? 'нет данных' : runtimeReadiness?.backend?.ready ? 'готов' : `прогрев (${formatSecondsRemaining(backendRemainingSeconds)})`}
        </span>
        <span className="rounded-full bg-white/70 px-2 py-1">
          AI agent: {!hasReadinessData ? 'нет данных' : runtimeReadiness?.ai_agent?.reachable ? (runtimeReadiness?.ai_agent?.warmed_up ? 'готов' : `прогрев (${formatSecondsRemaining(aiAgentRemainingSeconds)})`) : 'недоступен'}
        </span>
        <span className="rounded-full bg-white/70 px-2 py-1">
          Настройки: {!hasReadinessData ? 'нет данных' : (runtimeReadiness?.tenant_settings?.missing || []).length > 0 ? 'частично' : 'готовы'}
        </span>
      </div>
      {!runtimeReadinessLoading && !isReady && blockingReasons.length > 0 && (
        <div className="w-full text-xs opacity-80">
          Блокеры: {blockingReasons.join(', ')}
        </div>
      )}
      {!runtimeReadinessLoading && warningReasons.length > 0 && (
        <div className="w-full text-xs opacity-80">
          Предупреждения: {warningReasons.join(', ')}
        </div>
      )}
    </div>
  );

  return (
    <div className="bg-gray-100 p-6 min-h-screen">
      <div className="max-w-7xl mx-auto mb-4 flex flex-wrap items-center justify-between gap-3">
        {tab === 'models' ? runtimeReadinessBlock : null}
        <div className="inline-flex items-center rounded-full bg-slate-200/70 p-1">
          <button
            type="button"
            onClick={() => setTab('recommendations')}
            className={`rounded-full px-4 py-2 text-sm font-medium transition ${
              tab === 'recommendations'
                ? 'bg-white text-slate-900 shadow-sm'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            Рекомендации
          </button>
          <button
            type="button"
            onClick={() => setTab('stocks')}
            className={`rounded-full px-4 py-2 text-sm font-medium transition ${
              tab === 'stocks'
                ? 'bg-white text-slate-900 shadow-sm'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            Остатки
          </button>
          <button
            type="button"
            onClick={() => setTab('products')}
            className={`rounded-full px-4 py-2 text-sm font-medium transition ${
              tab === 'products'
                ? 'bg-white text-slate-900 shadow-sm'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            Товар
          </button>
          <button
            type="button"
            onClick={() => setTab('competitors')}
            className={`rounded-full px-4 py-2 text-sm font-medium transition ${
              tab === 'competitors'
                ? 'bg-white text-slate-900 shadow-sm'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            Конкуренты
          </button>
          <button
            type="button"
            onClick={() => setTab('ads')}
            className={`rounded-full px-4 py-2 text-sm font-medium transition ${
              tab === 'ads'
                ? 'bg-white text-slate-900 shadow-sm'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            Реклама
          </button>
          <button
            type="button"
            onClick={() => setTab('retro')}
            className={`rounded-full px-4 py-2 text-sm font-medium transition ${
              tab === 'retro'
                ? 'bg-white text-slate-900 shadow-sm'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            Ретро
          </button>
          <button
            type="button"
            onClick={() => setTab('returns')}
            className={`rounded-full px-4 py-2 text-sm font-medium transition ${
              tab === 'returns'
                ? 'bg-white text-slate-900 shadow-sm'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            Возвраты
          </button>
          <button
            type="button"
            onClick={() => setTab('models')}
            className={`rounded-full px-4 py-2 text-sm font-medium transition ${
              tab === 'models'
                ? 'bg-white text-slate-900 shadow-sm'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            Модели
          </button>
          <button
            type="button"
            onClick={() => setTab('settings')}
            className={`rounded-full px-4 py-2 text-sm font-medium transition ${
              tab === 'settings'
                ? 'bg-white text-slate-900 shadow-sm'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            Настройки
          </button>
        </div>
        <button
          type="button"
          onClick={() => setPriceCalcOpen(true)}
          className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm transition hover:border-blue-300 hover:text-blue-700"
          title="Калькулятор цены Pro"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4">
            <path fillRule="evenodd" d="M5 2a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2H5Zm1.5 3a.5.5 0 0 0-.5.5v2a.5.5 0 0 0 .5.5h7a.5.5 0 0 0 .5-.5v-2a.5.5 0 0 0-.5-.5h-7Zm0 5.5a.75.75 0 1 0 0 1.5.75.75 0 0 0 0-1.5Zm3 0a.75.75 0 1 0 0 1.5.75.75 0 0 0 0-1.5Zm3 0a.75.75 0 1 0 0 1.5.75.75 0 0 0 0-1.5ZM6.5 13.5a.75.75 0 1 0 0 1.5.75.75 0 0 0 0-1.5Zm3 0a.75.75 0 1 0 0 1.5.75.75 0 0 0 0-1.5Zm3 0a.75.75 0 1 0 0 1.5.75.75 0 0 0 0-1.5Z" clipRule="evenodd" />
          </svg>
          Калькулятор цены
        </button>
      </div>

      <Suspense fallback={tabContentFallback}>
        {tab === 'recommendations' && <AIRecommendationsBlock runtimeReadiness={runtimeReadiness} />}
        {tab === 'stocks' && <AIClusterSupplyBlock runtimeReadiness={runtimeReadiness} />}
        {tab === 'products' && (
          <>
            <AIProductsMonitorBlock />
            <AIProductCardHistoryBlock />
          </>
        )}
        {tab === 'competitors' && <AICompetitorCandidatesBlock />}
        {tab === 'ads' && <AIPromoMonitorBlock mode="ads" />}
        {tab === 'retro' && (
          <>
            <AIHypothesesMemoryBlock />
            <AIRetrospectiveBlock />
          </>
        )}
        {tab === 'returns' && <AIReturnsAnalyticsBlock />}
        {tab === 'models' && (
          <>
            <AIMLDemandSummaryBlock />
            <AIPromoMonitorBlock mode="models" />
            <AIContentMLSummaryBlock />
          </>
        )}
        {tab === 'settings' && (
          <>
            <AIAdminSettingsBlock />
            <AIExclusionsBlock />
          </>
        )}
        {isPriceCalcOpen && (
          <PriceCalculatorModal isOpen={isPriceCalcOpen} onClose={() => setPriceCalcOpen(false)} />
        )}
      </Suspense>
    </div>
  );
};

export default AIAgentsPage;
